import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { BOOKINGS } from "@/constants/testIds";
import { useSectionTheme } from "@/hooks/useSectionTheme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, CalendarClock, Undo2, Link as LinkIcon, Send } from "lucide-react";
import { toast } from "sonner";
import { DamageFlagBadge } from "@/components/DamageExtras";
import { DialogTour, DialogTourButton, keepDialogOpenForTour } from "@/components/PageTour";

const REFUND_REASONS = [
  { value: "guest_illness", label: "Guest illness" },
  { value: "double_booking", label: "Double-booking" },
  { value: "property_issue", label: "Property issue" },
  { value: "guest_request", label: "Guest requested" },
  { value: "goodwill", label: "Goodwill gesture" },
  { value: "other", label: "Other" },
];

export default function Bookings() {
  const sectionTheme = useSectionTheme("bookings");
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [icalToken, setIcalToken] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    contact_id: "",
    property_id: "",
    check_in: "",
    check_out: "",
    guests_count: 1,
    notes: "",
    total_price: 0,
    referred_by_code: "",
  });

  const load = () => {
    api.get("/bookings").then((r) => setRows(r.data));
    api.get("/contacts", { params: { type: "guest" } }).then((r) => setContacts(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/ical/token").then((r) => setIcalToken(r.data.token));
  };

  const copyIcal = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ical/all.ics?token=${icalToken}`;
    navigator.clipboard.writeText(url);
    toast.success("iCal link copied — cleaners can subscribe in their calendar app");
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editingId) {
        await api.put(`/bookings/${editingId}`, form);
        toast.success("Booking updated (synced to Hostaway)");
      } else {
        const { data } = await api.post("/bookings", form);
        if (data?.discount_amount) {
          toast.success(
            `Booking added — £${data.discount_amount.toFixed(2)} referral discount applied`,
          );
        } else {
          toast.success("Booking added");
        }
      }
      setOpen(false);
      setEditingId(null);
      setForm({
        contact_id: "",
        property_id: "",
        check_in: "",
        check_out: "",
        guests_count: 1,
        notes: "",
        total_price: 0,
        referred_by_code: "",
      });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      contact_id: b.contact_id,
      property_id: b.property_id,
      check_in: b.check_in || "",
      check_out: b.check_out || "",
      guests_count: b.guests_count || 1,
      notes: b.notes || "",
      total_price: b.total_price || 0,
      referred_by_code: b.referred_by_code || "",
    });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/bookings/${id}`);
    load();
  };

  const [refundTarget, setRefundTarget] = useState(null);
  const [refundReason, setRefundReason] = useState("guest_request");
  const [refundAmount, setRefundAmount] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);
  const [journeyTarget, setJourneyTarget] = useState(null);
  const [journeyForm, setJourneyForm] = useState({ deposit_amount: 250, require_id: true, require_esign: true, require_deposit: true, require_balance: true });
  const [sendingJourney, setSendingJourney] = useState(false);
  const [boostPreview, setBoostPreview] = useState(null);
  useEffect(() => {
    if (!journeyTarget?.damage_flag || !journeyForm.require_deposit) { setBoostPreview(null); return; }
    api.get(`/bookings/${journeyTarget.id}/deposit-boost`, { params: { base: Number(journeyForm.deposit_amount) || 0 } })
      .then((r) => setBoostPreview(r.data.boost)).catch(() => setBoostPreview(null));
  }, [journeyTarget, journeyForm.deposit_amount, journeyForm.require_deposit]);

  const openJourney = (b) => {
    setJourneyTarget(b);
    setJourneyForm({
      deposit_amount: 250,
      require_id: true,
      require_esign: true,
      require_deposit: true,
      require_balance: (b.total_price || 0) > 0,
    });
  };

  const createAndSendJourney = async () => {
    if (!journeyTarget) return;
    setSendingJourney(true);
    try {
      const { data: j } = await api.post("/journeys", {
        booking_id: journeyTarget.id,
        deposit_amount: Number(journeyForm.deposit_amount) || 0,
        require_id: journeyForm.require_id,
        require_esign: journeyForm.require_esign,
        require_deposit: journeyForm.require_deposit,
        require_balance: journeyForm.require_balance,
      });
      const { data: res } = await api.post(`/journeys/${j.id}/send`);
      toast.success(`Guest journey emailed to ${res.sent_to}`);
      if (j.deposit_boost) toast.warning(`${j.deposit_boost.reason} New deposit £${j.deposit_boost.amount.toFixed(2)}.`);
      setJourneyTarget(null);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSendingJourney(false);
    }
  };

  const copyJourneyLink = async (b) => {
    try {
      const { data: j } = await api.post("/journeys", { booking_id: b.id });
      const link = `${window.location.origin}/journey/${j.token}`;
      await navigator.clipboard.writeText(link);
      toast.success("Guest check-in link copied to clipboard");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openRefund = (b) => {
    setRefundTarget(b);
    setRefundReason("guest_request");
    setRefundAmount(b.total_price ? String(b.total_price.toFixed(2)) : "");
  };

  const confirmRefund = async () => {
    if (!refundTarget) return;
    const total = Number(refundTarget.total_price || 0);
    const amountNum = Number(refundAmount);
    if (!amountNum || amountNum <= 0) {
      toast.error("Enter a refund amount greater than zero");
      return;
    }
    if (amountNum > total + 0.005) {
      toast.error(`Amount can't exceed £${total.toFixed(2)}`);
      return;
    }
    const partial = Math.abs(amountNum - total) > 0.01;
    try {
      const { data } = await api.post(`/bookings/${refundTarget.id}/refund`, {
        reason: refundReason,
        amount: amountNum,
      });
      toast.success(
        partial
          ? `£${data.amount.toFixed(2)} partially refunded (£${data.total_refunded.toFixed(2)} of £${data.total_paid.toFixed(2)})`
          : `£${data.amount.toFixed(2)} refunded — stay cancelled`,
      );
      setRefundTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Bookings"
      subtitle="Every stay across your serviced homes — past, present and upcoming."
      actions={
        <>
          <Button
            variant="outline"
            onClick={copyIcal}
            className="rounded-full h-11 px-5"
          >
            <CalendarClock className="w-4 h-4 mr-2" /> Cleaners&apos; iCal
          </Button>
          <Button
            data-testid={BOOKINGS.addBtn}
            onClick={() => setOpen(true)}
            className="rounded-full ns-btn-primary h-11 px-6"
          >
            <Plus className="w-4 h-4 mr-2" /> New booking
          </Button>
        </>
      }
    >
      <section className={`ns-section ns-section-${sectionTheme} mb-5`} data-section-theme={sectionTheme}>
        <div className="flex items-start gap-3">
          <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[2.5rem]`} />
          <div className="flex-1 min-w-0">
            <div className="ns-label text-teal-800">Reservations</div>
            <div className="font-serif text-lg text-slate-900 mt-0.5">Every stay on one line</div>
            <div className="text-xs text-slate-500 mt-1">
              Rows carry a thin coloured rail — teal for confirmed, red for cancelled, grey for past.
              Direct bookings keep the Newport Stays margin; OTA rows show their origin.
            </div>
          </div>
        </div>
      </section>

      <div data-testid={BOOKINGS.root} className="ns-card overflow-hidden">
        <table data-testid={BOOKINGS.table} className="w-full">
          <thead>
            <tr className="text-left border-b border-slate-100 bg-stone-50/60">
              <th className="ns-label px-6 py-4">Guest</th>
              <th className="ns-label px-6 py-4">Property</th>
              <th className="ns-label px-6 py-4">Check-in</th>
              <th className="ns-label px-6 py-4">Check-out</th>
              <th className="ns-label px-6 py-4">Guests</th>
              <th className="ns-label px-6 py-4 text-right">Total</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center text-slate-400">
                  No bookings yet.
                </td>
              </tr>
            )}
            {rows.map((b) => {
              const isCancelled = b.status === "cancelled";
              const isPast = !isCancelled && b.check_out && new Date(b.check_out) < new Date();
              const rowClass = isCancelled ? "ns-row-cancelled" : isPast ? "ns-row-past" : "ns-row-confirmed";
              return (
              <tr key={b.id} className={`border-b border-slate-100 hover:bg-teal-50/30 ${rowClass}`}>
                <td className="px-6 py-4">
                  <Link to={`/contacts/${b.contact_id}`} className="hover:text-teal-700">
                    <div className="font-medium">
                      {b.contact_name}
                      <DamageFlagBadge flag={b.damage_flag} testId={`booking-damage-flag-${b.id}`} />
                      {b.status === "cancelled" && (
                        <span className="ml-2 text-[10px] tracking-widest uppercase text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded">
                          cancelled
                        </span>
                      )}
                      {b.channel === "Direct" && (
                        <span className="ml-2 text-[10px] tracking-widest uppercase text-teal-700 bg-teal-50 border border-teal-100 px-1.5 py-0.5 rounded">
                          direct
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{b.contact_email}</div>
                  </Link>
                </td>
                <td className="px-6 py-4 text-sm">{b.property_name}</td>
                <td className="px-6 py-4 text-sm">{b.check_in}</td>
                <td className="px-6 py-4 text-sm">{b.check_out}</td>
                <td className="px-6 py-4 text-sm">{b.guests_count}</td>
                <td className="px-6 py-4 text-sm text-right">
                  {b.total_price ? `£${Number(b.total_price).toFixed(2)}` : "—"}
                  {b.refunded_amount > 0 && (
                    <div className="text-[11px] text-red-600 mt-0.5">
                      −£{b.refunded_amount.toFixed(2)} refunded
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-right">
                  {b.contact_email && b.status !== "cancelled" && (
                    <>
                      <button
                        data-testid={`bookings-journey-${b.id}`}
                        onClick={() => openJourney(b)}
                        className="text-slate-300 hover:text-teal-700 p-1.5 mr-1"
                        title="Send guest check-in link"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`bookings-journey-copy-${b.id}`}
                        onClick={() => copyJourneyLink(b)}
                        className="text-slate-300 hover:text-teal-700 p-1.5 mr-1"
                        title="Copy guest check-in link"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {b.stripe_session_id && b.status !== "cancelled" && (
                    <button
                      data-testid={`bookings-refund-${b.id}`}
                      onClick={() => openRefund(b)}
                      className="text-slate-300 hover:text-amber-600 p-1.5 mr-1"
                      title="Refund via Stripe"
                    >
                      <Undo2 className="w-4 h-4" />
                    </button>
                  )}
                  {b.refund_history && b.refund_history.length > 0 && (
                    <button
                      data-testid={`bookings-refund-history-${b.id}`}
                      onClick={() => setHistoryTarget(b)}
                      className="text-[11px] tracking-wider uppercase text-teal-700 hover:underline px-2"
                      title="View refund history"
                    >
                      {b.refund_history.length} refund{b.refund_history.length === 1 ? "" : "s"}
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(b)}
                    className="text-slate-300 hover:text-teal-700 p-1.5 mr-1"
                    title="Edit dates"
                  >
                    ✎
                  </button>
                  <button
                    onClick={() => del(b.id)}
                    className="text-slate-300 hover:text-red-600 p-1.5"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto" onInteractOutside={keepDialogOpenForTour}>
          <DialogTour open={open} tourKey="/bookings#new" />
          <DialogHeader className="flex-row items-center justify-between gap-3 pr-8">
            <DialogTitle className="font-serif text-2xl">New booking</DialogTitle>
            <DialogTourButton tourKey="/bookings#new" />
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="ns-label">Guest</Label>
              <Select
                value={form.contact_id}
                onValueChange={(v) => setForm({ ...form, contact_id: v })}
              >
                <SelectTrigger data-testid={BOOKINGS.fieldContact} className="mt-2 h-11 bg-white">
                  <SelectValue placeholder="Select a guest" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} — {c.email || c.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="ns-label">Property</Label>
              <Select
                value={form.property_id}
                onValueChange={(v) => setForm({ ...form, property_id: v })}
              >
                <SelectTrigger data-testid={BOOKINGS.fieldProperty} className="mt-2 h-11 bg-white">
                  <SelectValue placeholder="Select a unit" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="bookings-field-dates">
              <div>
                <Label className="ns-label">Check-in</Label>
                <Input
                  type="date"
                  className="mt-2 h-11 bg-white"
                  value={form.check_in}
                  onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                />
              </div>
              <div>
                <Label className="ns-label">Check-out</Label>
                <Input
                  type="date"
                  className="mt-2 h-11 bg-white"
                  value={form.check_out}
                  onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="ns-label">Guests</Label>
              <Input
                type="number"
                min={1}
                className="mt-2 h-11 bg-white"
                value={form.guests_count}
                onChange={(e) => setForm({ ...form, guests_count: Number(e.target.value) })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="ns-label">Total price (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  data-testid="bookings-total-price"
                  className="mt-2 h-11 bg-white"
                  value={form.total_price}
                  onChange={(e) => setForm({ ...form, total_price: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label className="ns-label">Referral code (optional)</Label>
                <Input
                  data-testid="bookings-referral-code"
                  className="mt-2 h-11 bg-white font-mono"
                  placeholder="e.g. 3omcGGeR"
                  value={form.referred_by_code}
                  onChange={(e) => setForm({ ...form, referred_by_code: e.target.value })}
                />
                <div className="text-[11px] text-slate-400 mt-1.5">
                  10% comes off automatically when a valid guest referral code is used.
                </div>
              </div>
            </div>
            <div>
              <Label className="ns-label">Notes</Label>
              <Textarea
                className="mt-2 bg-white"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              data-testid={BOOKINGS.saveBtn}
              className="rounded-full ns-btn-primary px-6"
            >
              Save booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!refundTarget} onOpenChange={(v) => !v && setRefundTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Refund & cancel</DialogTitle>
          </DialogHeader>
          {refundTarget && (
            <div className="space-y-4 text-sm">
              <p className="text-slate-500">
                Refund <span className="font-medium text-slate-800">{refundTarget.contact_name}</span> for the stay at{" "}
                <span className="font-medium text-slate-800">{refundTarget.property_name}</span>. Paid{" "}
                <span className="font-medium">£{(refundTarget.total_price || 0).toFixed(2)}</span>.
              </p>
              <div>
                <Label className="ns-label">Refund amount (£)</Label>
                <Input
                  data-testid="bookings-refund-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={refundTarget.total_price || undefined}
                  className="mt-2 h-11 bg-white"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                />
                <div className="flex gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setRefundAmount(((refundTarget.total_price || 0) / 2).toFixed(2))}
                    className="text-[11px] tracking-wider uppercase text-teal-700 hover:underline"
                  >
                    Half
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefundAmount((refundTarget.total_price || 0).toFixed(2))}
                    className="text-[11px] tracking-wider uppercase text-teal-700 hover:underline"
                  >
                    Full
                  </button>
                </div>
              </div>
              <div>
                <Label className="ns-label">Reason</Label>
                <Select value={refundReason} onValueChange={setRefundReason}>
                  <SelectTrigger data-testid="bookings-refund-reason" className="mt-2 h-11 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REFUND_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-400 mt-2">
                  Partial refunds keep the booking marked "partially refunded" — full refunds cancel the stay.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefundTarget(null)}>
              Cancel
            </Button>
            <Button
              data-testid="bookings-refund-confirm"
              onClick={confirmRefund}
              className="rounded-full ns-btn-primary px-6"
            >
              Refund now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(v) => !v && setHistoryTarget(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Refund history</DialogTitle>
          </DialogHeader>
          {historyTarget && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-500">
                {historyTarget.contact_name} · {historyTarget.property_name}
              </div>
              <div className="border border-slate-100 rounded-xl">
                {(historyTarget.refund_history || []).map((h, i) => (
                  <div
                    key={h.id || i}
                    data-testid={`refund-history-item-${i}`}
                    className={`p-4 flex items-start gap-4 ${i > 0 ? "border-t border-slate-100" : ""}`}
                  >
                    <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">£{Number(h.amount).toFixed(2)}</div>
                        <div className="text-[10px] tracking-widest uppercase text-slate-400">
                          {h.partial ? "Partial" : "Full"}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {(h.reason || "other").replace(/_/g, " ")}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        {new Date(h.at).toLocaleString()} · by {h.by || "system"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ns-hairline" />
              <div className="grid grid-cols-2 text-sm">
                <div className="text-slate-500">Paid</div>
                <div className="text-right">£{Number(historyTarget.total_price || 0).toFixed(2)}</div>
                <div className="text-slate-500">Refunded</div>
                <div className="text-right text-red-600">
                  −£{Number(historyTarget.refunded_amount || 0).toFixed(2)}
                </div>
                <div className="pt-2 border-t border-slate-100 font-serif">Net kept</div>
                <div className="pt-2 border-t border-slate-100 font-serif text-right">
                  £{(Number(historyTarget.total_price || 0) - Number(historyTarget.refunded_amount || 0)).toFixed(2)}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHistoryTarget(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!journeyTarget} onOpenChange={(v) => !v && setJourneyTarget(null)}>
        <DialogContent className="bg-white max-w-md" onInteractOutside={keepDialogOpenForTour}>
          <DialogTour open={!!journeyTarget} tourKey="/bookings#journey" />
          <DialogHeader className="flex-row items-center justify-between gap-3 pr-8">
            <DialogTitle className="font-serif text-2xl">Send guest check-in link</DialogTitle>
            <DialogTourButton tourKey="/bookings#journey" />
          </DialogHeader>
          {journeyTarget && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-500">
                Emails {journeyTarget.contact_name} at{" "}
                <span className="text-slate-700 font-medium">{journeyTarget.contact_email}</span> a single link
                to complete pre-arrival, pay, authorise a deposit and browse extras.
              </div>
              <div>
                <Label className="ns-label">Damage-deposit hold (£)</Label>
                <Input
                  data-testid="journey-deposit-amount"
                  type="number"
                  step="0.01"
                  min={0}
                  className="mt-2 h-11 bg-white"
                  value={journeyForm.deposit_amount}
                  onChange={(e) => setJourneyForm({ ...journeyForm, deposit_amount: e.target.value })}
                />
                {boostPreview && (
                  <div className="mt-2 text-xs text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="journey-deposit-boost-note">
                    {boostPreview.reason} The guest will be asked for <strong>£{boostPreview.amount.toFixed(2)}</strong>.
                  </div>
                )}
              </div>
              <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="journey-steps">
                {[
                  ["require_id", "Ask for photo ID"],
                  ["require_esign", "E-sign house rules"],
                  ["require_deposit", "Authorise damage deposit"],
                  ["require_balance", `Take remaining balance${journeyTarget.total_price ? ` (£${Number(journeyTarget.total_price).toFixed(2)})` : ""}`],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!journeyForm[k]}
                      onChange={(e) => setJourneyForm({ ...journeyForm, [k]: e.target.checked })}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setJourneyTarget(null)}>Cancel</Button>
            <Button
              data-testid="journey-send-btn"
              onClick={createAndSendJourney}
              disabled={sendingJourney}
              className="rounded-full ns-btn-primary px-6"
            >
              {sendingJourney ? "Sending…" : "Send link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
